// Third-Party
import { AppBar, Box, Stack, Toolbar } from "@mui/material";
import { styled } from "@mui/material/styles";
import { RootState, store, useSelector } from "@redux/store";

// Internal
import Label from "@components/Label";
import useLocales from "@hooks/useLocales";
import { AskDost } from "@pages/askDost/components";
import SageLogo from "src/assets/logo/Sage-logo.svg";
import { useBanner } from "src/contexts/BannerContext";
import NotificationPopover from "src/pages/Notification/NotificationPopover";
import Iconify from "../../../components/Iconify";
import Logo from "../../../components/Logo";
import { IconButtonAnimate } from "../../../components/animate";
import { HEADER, NAVBAR } from "../../../config";
import useOffSetTop from "../../../hooks/useOffSetTop";
import useResponsive from "../../../hooks/useResponsive";
import cssStyles from "../../../utils/cssStyles";
import LanguagePopover from "./LanguagePopover";

// Types
import { CustomTheme } from "src/@types/customTheme";

type RootStyleProps = {
  isCollapse: boolean;
  isOffset: boolean;
  verticalLayout: boolean;
};

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== "isCollapse" && prop !== "isOffset" && prop !== "verticalLayout",
})<RootStyleProps>(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: "none",
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  top: 40, // 👈 shift header below the banner
  transition: theme.transitions.create(["width", "height", "top"], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up("lg")]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    paddingBottom: "4px",
    paddingTop: "4px",
    borderBottom: `1px solid ${theme.palette.text.borderLine}`,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: "100%",
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

// ----------------------------------------------------------------------

type Props = {
  onOpenSidebar: VoidFunction;
  isCollapse?: boolean;
  verticalLayout?: boolean;
};

export default function DashboardHeader({
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false,
}: Props) {
  const {
    org: { enableAskDost },
  } = store.getState()?.organization;

  const isOffset =
    useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  const { visible } = useBanner();
  const isDesktop = useResponsive("up", "lg");
  const customTheme = useSelector(
    (state: RootState) => state.accountInfo.accountInfo?.customTheme,
  );
  const isTestingMode = useSelector(
    (state: RootState) => state.organization.org.isTestingUser,
  );
  const { translate } = useLocales();

  return (
    <>
      <RootStyle
        isCollapse={isCollapse}
        isOffset={isOffset}
        verticalLayout={verticalLayout}
        sx={{ top: visible ? 40 : 0 }}
      >
        <Toolbar
          sx={{
            minHeight: "100% !important",
            px: { lg: 5 },
          }}
        >
          {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

          {!isDesktop && (
            <IconButtonAnimate
              onClick={onOpenSidebar}
              sx={{ mr: 1, color: "text.primary" }}
            >
              <Iconify icon="eva:menu-2-fill" />
            </IconButtonAnimate>
          )}
          <Box sx={{ flexGrow: 1 }} />

          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "right" }}
            spacing={{ xs: 0.5, sm: 1.5 }}
          >
            {isTestingMode && (
              <Label
                variant="outlined"
                color="warning"
                sx={{ alignSelf: "center" }}
              >
                {translate("testing_mode")}
              </Label>
            )}
            {enableAskDost && <AskDost />}
            <NotificationPopover />
            <LanguagePopover />
            {/* render sage logo for sage account only */}
            {customTheme === CustomTheme.Sage && (
              <Box sx={{ position: "relative", top: "8px" }}>
                <img src={SageLogo} style={{ width: "70px" }} alt="Sage logo" />
              </Box>
            )}
          </Stack>
        </Toolbar>
      </RootStyle>
    </>
  );
}
